import { useContext, useEffect, useState, useCallback } from "react";
import Contexts from "../../../contexts/Contexts";
import { useNavigate, useLocation } from "react-router";
import { Avatar, Tooltip } from "@mui/joy";
import CartSolidIcon from "../../Icon/CartSolidIcon";
import { cartAnimBus } from "../../CartAddAnimation";
import styles from "./index.module.css";
import Button from "../../Button";
import GroupsFilter from "../../../pages/Groups/components/GroupsFilter";
import BellIcon from "../../Icon/BellIcon";
import GiftBell from "./GiftBell";
import { useGiftingGate } from "../../../utils/giftingFlag";
import HamburgerMenu, { HamburgerMenuTrigger } from "../HamburgerMenu";
import { useTranslation } from "react-i18next";
import FloatLabelInput from "../../FloatLabelInput";
import SearchIcon from "../../Icon/SearchIcon";
import { useUserBalance } from "../../../api/hooks/user";
import { anonCartCount, anonCartBus } from "../../../utils/anonCart";
import { StrToCurrency } from "../../../api/ApiGame";

const Header: React.FC<{ showFilter?: boolean }> = ({ showFilter = false }) => {
  const {
    userId,
    cartCount,
    setIsLoginOpen,
    setIsRegistrationOpen,
    notificationsCount,
    user,
    setPostLoginRedirect,
    screenSize,
  } = useContext(Contexts);
  const giftingGate = useGiftingGate();

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [hamburgerOpen, setHamburgerOpen] = useState(false);
  const closeHamburger = useCallback(() => setHamburgerOpen(false), []);
  // Anonymous cart badge — reactive via anonCartBus (fires on every add/remove/clear)
  const [anonCount, setAnonCount] = useState<number>(() => (!userId ? anonCartCount() : 0));
  useEffect(() => {
    if (userId) { setAnonCount(0); return; }
    const unsub = anonCartBus.subscribe(() => setAnonCount(anonCartCount()));
    return () => unsub();
  }, [userId]);
  const { balance, fetchBalance } = useUserBalance();

  // Fetch balance once when user logs in
  useEffect(() => {
    if (userId) fetchBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const balanceAmount = balance?.amount ? parseFloat(balance.amount) : 0;
  // BUNDLE 4c: handleDeposit removed from customer-facing flows.
  // DepositDrawer component retained for internal/operational use only.

  // Get the user avatar from context, with fallback to default
  const userAvatar = user?.avatar || "/images/1321318062.png";
  const loggedIn = !!(userId && userId !== 0 && !user?.isGuest);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        {/* Mobile: search expanded — takes full width */}
        {!screenSize.isDesktop && searchOpen ? (
          <>
            <div className={styles.searchExpanded}>
              <SearchIcon size="16px" />
              <FloatLabelInput
                label={t("search.search")}
                style={{ flex: 1 }}
                endDecorator={null}
              />
            </div>
            <span className={styles.searchCancel} onClick={() => setSearchOpen(false)}>
              Cancelar
            </span>
          </>
        ) : (
          <>
            <div className={styles.logo} onClick={() => navigate("/")}>
              <picture>
                <source srcSet="/images/logo.webp" type="image/webp" />
                <img src="/images/logo.png" height={43} width={89} alt="Leve Sorte" fetchPriority="high" decoding="async" />
              </picture>
            </div>

            {/* Desktop: filter + search bar */}
            {screenSize.isDesktop && (
              <>
                <GroupsFilter />
                <FloatLabelInput
                  label={t("search.search")}
                  style={{ width: "393px" }}
                  endDecorator={<SearchIcon size="21px" />}
                />
              </>
            )}

            {/* Mobile: search icon */}
            {!screenSize.isDesktop && (
              <button className={styles.iconBtn} onClick={() => setSearchOpen(true)} aria-label={t("search.search")}>
                <SearchIcon size="20px" />
              </button>
            )}

            {/* Bell (logged in). Gate-on it is the two-indicator variant (Batch 5):
                the red unread badge keeps the far-right slot and the bell still opens
                the feed, with a 🎁 indicator alongside it — to its left — when a gift
                is pending.

                Strictly `=== "on"`, never optimistic: GiftBell fires GET /api/gift/
                unconditionally on mount, so mounting it while the gate is undetermined
                would hit a possibly-dark endpoint and trip the interceptor's global
                error snackbar on every page load. While undetermined (and always, in a
                flag-off build, where the gate short-circuits to "off" synchronously) this
                renders the untouched bell else-branch — character-identical to GiftBell's
                own no-gift state, so the swap is invisible. */}
            {giftingGate === "on" && loggedIn ? (
              <GiftBell notificationsCount={notificationsCount} />
            ) : (
              <button
                aria-label={t("nav.notifications") || "Notificações"}
                className={loggedIn ? styles.iconBtn : "display-none"}
                onClick={() => navigate("/notifications")}
              >
                <span className={styles.bellIconWrap}>
                  <BellIcon size="24px" color="#9AA9B3" />
                  {notificationsCount > 0 && <span className={styles.unreadDot} />}
                </span>
              </button>
            )}

            {/* Cart — always visible (anonymous users can view localStorage cart, authenticated see server cart) */}
            <button
              aria-label={t("nav.cart") || "Carrinho"}
              className={styles.iconBtn}
              onClick={() => navigate("/cart")}
            >
              {/* Bug 8b: red dot badge when cartCount > 0; ref registered for animation targeting */}
              <div ref={(el) => cartAnimBus.registerCartIcon(el)} style={{ position: "relative", display: "inline-flex" }}>
                <CartSolidIcon size="24px" />
                {(cartCount > 0 || (!loggedIn && anonCount > 0)) && (
                  <span
                    style={{
                      position: "absolute",
                      top: -2,
                      right: -2,
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#ff0000",
                      display: "block",
                    }}
                  />
                )}
              </div>
            </button>

            {/* Mobile: Prêmios (prize balance) pill — display only, no deposit tap (Bundle 4c) */}
            {/* PT-BR: "Prêmios" = prizes available for withdrawal */}
            {/* CLS fix: use visibility:hidden (not unmount) to reserve layout space when balance = 0 */}
            {!screenSize.isDesktop && loggedIn && (
              <Tooltip title="Estes são seus prêmios disponíveis para saque" placement="bottom" size="sm">
                <div className={styles.balancePill} style={{ cursor: "default", visibility: balanceAmount > 0 ? "visible" : "hidden" }}>
                  <span className={styles.bpAmount}>{StrToCurrency(balanceAmount)}</span>
                </div>
              </Tooltip>
            )}

            {/* Desktop: Prêmios chip — display only, no deposit tap (Bundle 4c) */}
            {/* CLS fix: use visibility:hidden to preserve layout */}
            {screenSize.isDesktop && loggedIn && (
              <Tooltip title="Estes são seus prêmios disponíveis para saque" placement="bottom" size="sm">
                <div className={styles.balanceChip} style={{ cursor: "default", visibility: balanceAmount > 0 ? "visible" : "hidden" }}>
                  <span className={styles.chipDot} />
                  {StrToCurrency(balanceAmount)}
                </div>
              </Tooltip>
            )}

            {/* Avatar (logged in) / Auth buttons (logged out) + hamburger (mobile) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {loggedIn ? (
                <Avatar src={userAvatar} onClick={() => navigate("/settings")} />
              ) : (
                <>
                  {/* Desktop: login + create account buttons */}
                  {screenSize.isDesktop && (
                    <>
                      <Button
                        type="secondary"
                        outlined
                        onClick={() => {
                          const isMiniGamePage = location.pathname.includes('/mini-games');
                          const isChallengePage = location.pathname.includes('/challenge/');
                          if (isMiniGamePage || isChallengePage) {
                            if (isChallengePage) {
                              const currentPath = location.pathname + location.search + location.hash;
                              setPostLoginRedirect(currentPath);
                            }
                            navigate("/");
                            setTimeout(() => { setIsLoginOpen(true); }, 100);
                          } else {
                            setIsLoginOpen(true);
                          }
                        }}
                      >
                        {t("auth.logIn")}
                      </Button>
                      <Button onClick={() => setIsRegistrationOpen(true)}>
                        {t("auth.createAccount")}
                      </Button>
                    </>
                  )}
                </>
              )}
              {/* Hamburger — mobile/tablet only */}
              {!screenSize.isDesktop && (
                <HamburgerMenuTrigger onClick={() => setHamburgerOpen(true)} />
              )}
            </div>
            <HamburgerMenu isOpen={hamburgerOpen} onClose={closeHamburger} />
          </>
        )}
      </div>
      {!screenSize.isDesktop && showFilter && <GroupsFilter />}
    </div>
  );
};
export default Header;
